// src/views/OrgDeletedPage.js
import React, { useEffect, useState } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { Building, RefreshCw, Trash2, LogOut, Clock, Mail } from 'lucide-react';

/**
 * Shown when a user's organization is in 'deleted' state.
 *
 * Two views, branched on whether the current user is the billing owner:
 *  - Billing owner: reinstate UI (placeholder buttons; full reinstate flow
 *    is built on mobile in Chunk 3D).
 *  - Other users: read-only "your org has been deleted, sign out" message.
 *
 * RequireAuth gates access to this page and ensures only signed-in users
 * with a deleted org reach it.
 */
export default function OrgDeletedPage() {
    const [user, setUser] = useState(null);
    const [orgData, setOrgData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const unsubAuth = onAuthStateChanged(auth, (u) => setUser(u));
        return () => unsubAuth();
    }, []);

    useEffect(() => {
        if (!user) return;
        const userRef = doc(db, 'users', user.uid);
        const unsubUser = onSnapshot(userRef, (snap) => {
            if (!snap.exists()) { setLoading(false); return; }
            const orgId = snap.data().organizationId;
            if (!orgId) { setLoading(false); return; }

            const orgRef = doc(db, 'organizations', orgId);
            const unsubOrg = onSnapshot(orgRef, (orgSnap) => {
                if (orgSnap.exists()) {
                    setOrgData({ ...orgSnap.data(), id: orgSnap.id });
                }
                setLoading(false);
            });
            return () => unsubOrg();
        });
        return () => unsubUser();
    }, [user]);

    const isBillingOwner = !!user && !!orgData && orgData.ownerUid === user.uid;

    const orgName = orgData?.companyName || 'your organization';
    const deletedAt = orgData?.deletedAt?.toDate?.();
    const deadline = deletedAt
        ? new Date(deletedAt.getTime() + 90 * 24 * 60 * 60 * 1000)
        : null;

    const formatDate = (date) => {
        if (!date) return '—';
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    };

    const handleSignOut = async () => {
        try { await signOut(auth); } catch (err) { console.warn('Sign out error:', err); }
    };

    const handleRestorePlaceholder = () => {
        alert(
            'Restoring your organization is now available in the Spatchy mobile app. ' +
            'Open the iOS or Android app and sign in to restore.'
        );
    };

    const handlePermanentDeletePlaceholder = () => {
        alert(
            'Immediate permanent deletion is now available in the Spatchy mobile app. ' +
            'Open the iOS or Android app and sign in to permanently delete.'
        );
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-gray-500 text-sm">Loading...</div>
            </div>
        );
    }

    // --- BILLING OWNER VIEW ---
    if (isBillingOwner) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col items-center py-12 px-4">
                <div className="w-full max-w-2xl">

                    {/* Header */}
                    <div className="text-center mb-8">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 rounded-full mb-4">
                            <Clock className="text-amber-600" size={28} />
                        </div>
                        <h1 className="text-3xl font-bold text-gray-900 mb-2">
                            Organization scheduled for deletion
                        </h1>
                        <p className="text-gray-600 max-w-md mx-auto">
                            <span className="font-semibold text-gray-900">{orgName}</span> was deleted
                            {deletedAt ? ` on ${formatDate(deletedAt)}` : ''}.
                            {deadline ? (
                                <span className="block mt-2">
                                    You have until <span className="font-semibold">{formatDate(deadline)}</span> to restore it. After that, your data will be permanently deleted.
                                </span>
                            ) : null}
                            {/* orgfiles-5f1d9b26 — same sentence as the deletion emails (Marcus, 2026-10-28) */}
                            <span className="block mt-2">Your financial records (invoices, payments, credit notes, refunds and payroll) are kept securely for 7 years as the law requires, then deleted. Nobody can see them in Spatchy.</span>
                        </p>
                    </div>

                    {/* RESTORE OPTION */}
                    <div className="bg-white rounded-xl border-2 border-blue-100 p-6 mb-4 shadow-sm">
                        <div className="flex items-start gap-4">
                            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                                <RefreshCw size={20} className="text-blue-600" />
                            </div>
                            <div className="flex-1">
                                <h2 className="text-lg font-bold text-gray-900 mb-1">Restore Organization</h2>
                                <p className="text-sm text-gray-600 mb-4">
                                    Continue using Spatchy. Your subscription will resume; we may need to confirm payment if it has lapsed.
                                </p>
                                <button
                                    onClick={handleRestorePlaceholder}
                                    className="w-full sm:w-auto px-6 py-2.5 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                                >
                                    Restore Organization
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* PERMANENT DELETE OPTION */}
                    <div className="bg-white rounded-xl border-2 border-red-100 p-6 mb-6 shadow-sm">
                        <div className="flex items-start gap-4">
                            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                                <Trash2 size={20} className="text-red-600" />
                            </div>
                            <div className="flex-1">
                                <h2 className="text-lg font-bold text-gray-900 mb-1">Permanently Delete Now</h2>
                                <p className="text-sm text-gray-600 mb-4">
                                    Skip the 90-day grace period. All data is deleted immediately. This cannot be undone.
                                </p>
                                <p className="text-sm text-gray-600 mb-4">{/* orgfiles-5f1d9b26 */}
                                    Your financial records (invoices, payments, credit notes, refunds and payroll) are kept securely for 7 years as the law requires, then deleted. Nobody can see them in Spatchy.
                                </p>
                                <button
                                    onClick={handlePermanentDeletePlaceholder}
                                    className="w-full sm:w-auto px-6 py-2.5 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors shadow-sm"
                                >
                                    Permanently Delete
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Decide later */}
                    <div className="text-center">
                        <p className="text-sm text-gray-500 mb-3">
                            Decide later? You can sign in again anytime within the 90-day window.
                        </p>
                        <button
                            onClick={handleSignOut}
                            className="inline-flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
                        >
                            <LogOut size={16} />
                            Sign Out
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // --- NON-BILLING-OWNER VIEW ---
    return (
        <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4 py-12">
            <div className="w-full max-w-md">

                {/* Header */}
                <div className="text-center mb-6">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                        <Building className="text-red-600" size={28} />
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">
                        Organization deleted
                    </h1>
                    <p className="text-gray-600">
                        The organization you belong to has been deleted by its owner. You no longer have access to Spatchy.
                    </p>
                </div>

                {/* Info card */}
                <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6 flex items-start gap-3 shadow-sm">
                    <Mail className="text-blue-600 shrink-0 mt-0.5" size={20} />
                    <div className="flex-1">
                        <p className="text-sm font-semibold text-gray-900 mb-1">Think this was a mistake?</p>
                        <p className="text-sm text-gray-600">
                            Please contact the organization owner directly. They can restore the organization within 90 days of deletion.
                        </p>
                    </div>
                </div>

                {/* Sign out */}
                <button
                    onClick={handleSignOut}
                    className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-gray-900 text-white font-semibold rounded-lg hover:bg-black transition-colors shadow-sm"
                >
                    <LogOut size={16} />
                    Sign Out
                </button>
            </div>
        </div>
    );
}